import Axios, {
  AxiosInstance,
  AxiosPromise,
  Method,
  AxiosHeaderValue,
} from 'axios';
import { get, omit } from 'lodash';
import qs from 'qs';
import { ApiEndpoint, ENDPOINTS } from '../constants/endpoints';
import { EndpointPayload, IApiRequest } from '../types/api';
import { appConfig } from '../../config';
import TOKEN from './token';
import { getXprops } from '../hooks/useXProps';

interface ApiHeaders {
  [key: string]: AxiosHeaderValue;
}

class ApiFactory {
  headers: ApiHeaders;
  client: AxiosInstance;
  endpoints: typeof ENDPOINTS;

  constructor(endpoints: typeof ENDPOINTS) {
    this.headers = {};
    this.endpoints = endpoints;
    this.client = Axios.create();
  }

  fetch = <T = IApiRequest>(
    endpoint: ApiEndpoint,
    data: EndpointPayload = {},
    sendInParams?: boolean,
  ): AxiosPromise<T> => {
    const response = this.generateFullUri(this.endpoints[endpoint].uri, data);
    let { url } = response;
    const { removed } = response;
    const method = this.endpoints[endpoint].method as Method;
    const body: EndpointPayload = omit(data, removed);
    const token = TOKEN.get();
    const tenant =
      window.sessionStorage.getItem('tenant') || getXprops().tenant;
    const isValidTenant = tenant.trim().length > 0 ? true : false;

    if (!isValidTenant) {
      const error = 'API Fetch: Invalid or empty tenant value';
      console.error(error);
      return Promise.reject(error);
    }

    const formDataBody = new FormData();

    if (token) {
      this.headers = { Authorization: `Bearer ${token}` };
    }
    if (tenant) {
      this.headers = { ...this.headers, 'X-Tenant': tenant };
    }

    if (get(this.endpoints[endpoint], 'isMultipart', false)) {
      this.headers['Content-Type'] = 'multipart/form-data';
      Object.keys(body).forEach((key) =>
        formDataBody.append(key, body[key] as Blob),
      );
    }

    if (method === 'GET' && !sendInParams) {
      const queryString = qs.stringify(body, { arrayFormat: 'repeat' });
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    return this.client({
      url,
      method,
      data: get(this.endpoints[endpoint], 'isMultipart', false)
        ? formDataBody
        : body,
      headers: this.headers,
    }).catch((e) => {
      console.error(e);
      throw e;
    });
  };

  private generateFullUri = (endpoint: string, data: EndpointPayload = {}) => {
    const removed: string[] = [];

    const url = `${appConfig.apiUrl}${endpoint}`.replace(
      /\{(.*?)\}/g,
      (token, name) => {
        let value = token;
        if (data && data[name]) {
          removed.push(name);
          value = (data[name] as string).toString();
        }
        return value;
      },
    );

    return { url, removed };
  };

  /* Tests */
  public reinitializeEndpoints = (endpoints: typeof ENDPOINTS) => {
    this.endpoints = endpoints;
  };
}

const api = new ApiFactory(ENDPOINTS);

export default api;
